import * as AmplitudeBrowser from '@amplitude/analytics-browser';
import AmplitudeLibrary from 'amplitude';

import {
    getAmplitudeApiKey,
    getAmplitudeDeviceIdFromLocalStorage,
    getAmplitudeDeviceIdFromServer,
    isAmplitudeBrowserSDK2Enabled,
} from 'chairisher/context/analytics';
import { getGuid, isBot, isIdentified, isImpersonated, isStaff } from 'chairisher/context/auth';

let amplitudeLibrary = null;
let isAmplitudeBrowserActive = false;

/**
 * @see https://amplitude.com/docs/sdks/analytics/browser/migrate-from-javascript-sdk-to-browser-sdk-2-0#identify
 * @todo (CHAIR-18484) - Remove wrapper function during migration cleanup and ensure getInstance() calls are removed.
 *
 * Returns the Identify constructor for the current SDK.
 * @returns {Object|null} the Identify constructor for the current SDK, or null if no SDK is available
 */
const getIdentifyConstructor = () => {
    if (isAmplitudeBrowserActive) {
        return AmplitudeBrowser.Identify;
    }

    if (amplitudeLibrary) {
        return amplitudeLibrary.getInstance().Identify;
    }

    console.error('no amplitude sdk available for getIdentifyConstructor call');

    return null;
};

/**
 * @see https://amplitude.com/docs/sdks/analytics/browser/migrate-from-javascript-sdk-to-browser-sdk-2-0#identify
 * @todo (CHAIR-18484) - Remove wrapper function during migration cleanup and ensure getInstance() calls are removed.
 *
 * Identifies the user for the current session.
 * @param {Object} identifyEvent the identify event to send
 */
const identify = (identifyEvent) => {
    if (isAmplitudeBrowserActive) {
        try {
            AmplitudeBrowser.identify(identifyEvent);
        } catch (e) {
            console.error('failed to call identify() on amplitude browser:', e);
        }
    } else if (amplitudeLibrary) {
        amplitudeLibrary.getInstance().identify(identifyEvent);
    } else {
        console.error('no amplitude sdk available for identify call');
    }
};

/**
 * @todo (CHAIR-18484) - Remove wrapper function during migration cleanup and implement functionality or explore alternatives.
 *
 * Note: no current session id indicates the sdk is not yet ready
 *
 * @returns {boolean} true if the current session is new for the user; otherwise, false
 */
const isNewSession = () => {
    if (isAmplitudeBrowserActive) {
        try {
            const lastSessionId = localStorage.getItem('amp_session_id');
            const currentSessionId = AmplitudeBrowser.getSessionId();

            if (!currentSessionId) {
                if (!lastSessionId) {
                    return true;
                }

                return false;
            }

            if (!lastSessionId) {
                localStorage.setItem('amp_session_id', String(currentSessionId));
                return true;
            }

            const isNewUserSession = String(currentSessionId) !== lastSessionId;
            if (isNewUserSession) {
                localStorage.setItem('amp_session_id', String(currentSessionId));
            }

            return isNewUserSession;
        } catch (e) {
            console.error('failed to determine isNewSession on amplitude browser, defaulting to true:', e);
            return true;
        }
    } else if (amplitudeLibrary) {
        return amplitudeLibrary.getInstance().isNewSession();
    } else {
        console.error('no amplitude sdk available to determine isNewSession, defaulting to true');
        return true;
    }
};

/**
 * @see https://amplitude.com/docs/sdks/analytics/browser/migrate-from-javascript-sdk-to-browser-sdk-2-0#logevent
 * @todo (CHAIR-18484) - Remove wrapper function during migration cleanup and replace getInstance().logEvent() with track().
 *
 * Sends an event to Amplitude.
 * @param {string} eventType the event type to send
 * @param {Object} eventProperties the event properties to send
 */
const logEvent = (eventType, eventProperties) => {
    if (isAmplitudeBrowserActive) {
        try {
            AmplitudeBrowser.track(eventType, eventProperties);
        } catch (e) {
            console.error('failed to call track() on amplitude browser:', e);
        }
    } else if (amplitudeLibrary) {
        amplitudeLibrary.getInstance().logEvent(eventType, eventProperties);
    } else {
        console.error('no amplitude sdk available for logEvent call');
    }
};

/**
 * @see https://amplitude.com/docs/sdks/analytics/browser/migrate-from-javascript-sdk-to-browser-sdk-2-0#setuserid
 * @todo (CHAIR-18484) - Remove wrapper function during migration cleanup and ensure getInstance() calls are removed.
 *
 * Sets the user ID for the current session.
 * @param {string} userId the user ID to set
 */
const setUserId = (userId) => {
    if (isAmplitudeBrowserActive) {
        try {
            AmplitudeBrowser.setUserId(userId);
        } catch (e) {
            console.error('failed to call setUserId() on amplitude browser:', e);
        }
    } else if (amplitudeLibrary) {
        amplitudeLibrary.getInstance().setUserId(userId);
    } else {
        console.error('no amplitude sdk available for setUserId call');
    }
};

/**
 * @todo (CHAIR-18484) - Remove during migration cleanup once legacy SDK is no longer used.
 *
 * Initializes and stores the legacy Amplitude JS SDK.
 * @param {string} amplitudeApiKey the Amplitude API key which is shared across both SDKs
 */
const initializeLegacyAmplitude = (amplitudeApiKey) => {
    AmplitudeLibrary.init(amplitudeApiKey, null, {
        includeGclid: true,
        includeReferrer: true,
        includeUtm: true,
        saveParamsReferrerOncePerSession: false,
        unsetParamsReferrerOnNewSession: true,
    });

    amplitudeLibrary = AmplitudeLibrary;
};

/**
 * @todo (CHAIR-18484) - Remove references to legacy SDK during migration cleanup.
 *
 * Initializes the Amplitude SDK, either the legacy JS SDK or the new Browser SDK 2.
 * @returns {Object|null} the proxy object for the Amplitude SDKs, if conditions are met; otherwise, null
 */
export default function initializeAmplitude() {
    if (!isImpersonated() && !isStaff() && !isBot()) {
        const amplitudeApiKey = getAmplitudeApiKey();

        if (amplitudeApiKey) {
            try {
                if (!isAmplitudeBrowserSDK2Enabled()) {
                    // Amplitude JS SDK
                    initializeLegacyAmplitude(amplitudeApiKey);
                } else {
                    // Amplitude Browser SDK 2
                    try {
                        let deviceId = getAmplitudeDeviceIdFromLocalStorage();

                        if (!deviceId) {
                            deviceId = getAmplitudeDeviceIdFromServer();
                        }

                        AmplitudeBrowser.init(amplitudeApiKey, {
                            // @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture
                            autocapture: {
                                attribution: true,
                                pageViews: false,
                                sessions: false,
                                formInteractions: false,
                                fileDownloads: false,
                                // all of the above event types are true by default (attribution added for clarity)
                                // elementInteractions, frustrationInteractions, and networkTracking are false by default
                            },
                            deviceId,
                            userId: isIdentified() ? getGuid() : undefined,
                        });

                        isAmplitudeBrowserActive = true;
                    } catch (e) {
                        console.error('failed to initialize amplitude browser:', e);
                        initializeLegacyAmplitude(amplitudeApiKey);
                    }
                }

                return {
                    getInstance: () => ({
                        Identify: getIdentifyConstructor(),
                        identify,
                        isNewSession,
                        logEvent,
                        setUserId,
                    }),
                };
            } catch (e) {
                console.error('failed to initialize amplitude:', e);
            }
        }
    }

    return null;
}
